import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SignIn, CircleNotch } from '@phosphor-icons/react';

function formatApiError(detail) {
    if (detail == null) return 'Coś poszło nie tak. Spróbuj ponownie.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail))
        return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
}

export default function Login() {
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login: authLogin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Surface a friendly reason banner (?reason=idle → auto-logout, etc.)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const reason = params.get('reason');
        if (reason === 'idle') {
            setError('Wylogowano automatycznie z powodu bezczynności.');
        } else if (reason === 'locked') {
            setError('Konto zablokowane po zbyt wielu nieudanych logowaniach. Spróbuj za 30 min.');
        }
    }, [location.search]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const user = await authLogin(login, password);
            if (user.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <div className="mb-10">
                        <h1 className="font-heading text-4xl font-extrabold tracking-tight mb-2" data-testid="login-title">
                            Fleetly
                        </h1>
                        <p className="text-muted-foreground">
                            Zarządzaj flotą, zespołem i firmą
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-sm text-sm" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <div>
                            <label className="field-label block mb-2">Login</label>
                            <input
                                type="text"
                                value={login}
                                onChange={(e) => setLogin(e.target.value)}
                                className="input-field"
                                placeholder="Wpisz login (np. 17d)"
                                required
                                data-testid="login-email-input"
                            />
                        </div>

                        <div>
                            <label className="field-label block mb-2">Hasło</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field"
                                placeholder="••••••••"
                                required
                                data-testid="login-password-input"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full flex items-center justify-center gap-2"
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <CircleNotch className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    <SignIn className="w-5 h-5" />
                                    Zaloguj się
                                </>
                            )}
                        </button>
                    </form>
                    <div className="mt-6 text-center text-sm text-muted-foreground">
                        Nie masz konta?{" "}
                        <a href="/register" className="text-primary hover:underline font-medium" data-testid="login-register-link">
                            Zarejestruj się z kodem
                        </a>
                    </div>
                </div>
            </div>

            {/* Right side - Image */}
            <div className="hidden lg:block flex-1 login-bg" />
        </div>
    );
}
